import { useCallback, useEffect, useState } from 'react';

import { Box } from '@mui/material';
import CandidateSettingCodeEnum from '@settings/models/candidate-codes.enum';
import CheckboxSetting from '../atoms/CheckboxSetting';
import JNumberField from '@atoms/JNumberField';
import JTextFieldBase from '@atoms/JTextFieldBase';
import Jpaper from '@atoms/JPaper';
import { Loading } from '@atoms/Loading';
import NumberSetting from '../atoms/NumberSetting';
import RomeDomainsOrder from '../RomeDomainsOrder';
import UpdateSettingDto from '@settings/models/dto/update-setting.dto';
import { useGetCandidateSettings } from '@settings/queries/useGetSettings';
import useJConfirmationBar from '@/hooks/useJConfirmationBar';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useUpdateSettings } from '@settings/mutations/settingsMutations';

const CandidatesSettings = () => {
  const { data, isFetching: isSettingsFetching } = useGetCandidateSettings();
  const {
    mutate: mutateSettings,
    isLoading: isMutateSettingsLoading,
    isSuccess: isMutateSettingsSuccess,
  } = useUpdateSettings();
  const { show, close } = useJConfirmationBar();
  const queryClient = useQueryClient();
  const [payload, setPayload] = useState<UpdateSettingDto[]>([]);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [isDirty, setIsDirty] = useState(false);
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<
    CandidateSettingCodeEnum[]
  >([]);
  const settings = data?.data;

  const addValue = (code: CandidateSettingCodeEnum, value: string) => {
    setPayload((prev) => [...prev, { code, value }]);
  };
  const updateValue = (
    code: CandidateSettingCodeEnum,
    value: string,
    index: number,
  ) => {
    const updatedPayload = [...payload];
    updatedPayload[index] = { code, value };
    setPayload(updatedPayload);
  };

  const onChangeSetting = (code: CandidateSettingCodeEnum, value: string) => {
    // set is dirty
    if (!isDirty) {
      setIsDirty(true);
    }
    // Check if the code already exists in payload
    const existingIndex = payload.findIndex((item) => item.code === code);
    if (existingIndex !== -1) {
      updateValue(code, value, existingIndex);
    } else {
      addValue(code, value);
    }
  };

  const getDefaultValueByCode = (
    code: CandidateSettingCodeEnum,
  ): string | undefined => {
    return settings?.find((item) => item.code === code)?.defaultValue;
  };

  const getValueByCode = (code: CandidateSettingCodeEnum): number => {
    const defaultvalue = settings?.find((item) => item.code === code)?.value;
    const payloadValue = payload?.find((item) => item.code === code)?.value;
    return Number(payloadValue ?? defaultvalue);
  };
  const isFieldHidden = useCallback(
    (label: string, rank: number): boolean => {
      if (rank.toLocaleString() === searchText) return false;
      return Boolean(
        searchText &&
          !label.toLocaleLowerCase()?.includes(searchText.toLocaleLowerCase()),
      );
    },
    [searchText],
  );

  useEffect(() => {
    if (!isDirty) return;
    show({
      onClose: () => {
        queryClient.invalidateQueries(['settings-enterprise']);
      },
      onConfirm: () => mutateSettings({ payload }),
    });
  }, [isDirty, payload]);

  useEffect(() => {
    return () => close();
  }, []);

  useEffect(() => {
    if (!isMutateSettingsSuccess) return;
    close();
  }, [isMutateSettingsSuccess]);

  useEffect(() => {
    if (validationErrors.length === 0) return;
    close();
  }, [validationErrors.length]);

  const isFieldNonValid = ({
    code,
    minValue,
    maxValue,
  }: {
    code: CandidateSettingCodeEnum;
    minValue?: number;
    maxValue?: number;
  }): boolean => {
    const valueIncluded = validationErrors.includes(code);
    const val = getValueByCode(code);
    if (maxValue && val > maxValue) {
      if (!valueIncluded) setValidationErrors((prev) => [...prev, code]);
      return true;
    }
    if (minValue && val < minValue) {
      if (!valueIncluded) setValidationErrors((prev) => [...prev, code]);
      return true;
    }
    if (valueIncluded) {
      const updatedItems = validationErrors.filter((item) => code !== item);
      setValidationErrors(updatedItems);
    }
    return false;
  };

  const candSearchAreaRangeMinValidation = isFieldNonValid({
    code: CandidateSettingCodeEnum.candSearchAreaRangeMin,
    maxValue: getValueByCode(CandidateSettingCodeEnum.candSearchAreaRangeMax),
  });
  const candSearchAreaRangeMaxValidation = isFieldNonValid({
    code: CandidateSettingCodeEnum.candSearchAreaRangeMax,
    minValue: getValueByCode(CandidateSettingCodeEnum.candSearchAreaRangeMin),
  });

  const mostRecrtHidden = isFieldHidden(t('MOST_RECRT'), 1);
  const jobsListBySectorHidden = isFieldHidden(t('JOBS_LIST_BY_SECTOR'), 2);
  const sectorsOrderHidden = isFieldHidden(t('SECTORS_ORDER'), 3);
  const latestJobsHidden = isFieldHidden(t('LATEST_JOBS'), 4);
  const searchFieldLimitCharsHidden = isFieldHidden(
    `${t('SEARCH_FIELD')} ${t('LIMIT_CHARS')}`,
    5,
  );
  const searchSpaceHidden = isFieldHidden(`${t('SEARCH_SPACE')}`, 6);
  const similairJobsHidden = isFieldHidden(t('SIMILAIR_JOBS'), 7);
  const candidatSignUpProfilePicHidden = isFieldHidden(
    `${t('CANDIDAT_SIGN_UP')} : ${t('PROFILE_PIC')}`,
    8,
  );
  const candidatSignUpTagsHidden = isFieldHidden(
    `${t('CANDIDAT_SIGN_UP')} : ${t('TAGS')}`,
    9,
  );
  const candidatSignUpCvWrittenHidden = isFieldHidden(
    `${t('CANDIDAT_SIGN_UP')} : ${t('CV_WRITTEN')}`,
    10,
  );
  const delayCandLimitHidden = isFieldHidden(t('DELAY_CAND_LIMIT'), 11);
  const delayFavsLimitHidden = isFieldHidden(t('DELAY_FAVS_LIMIT'), 12);
  const maxQualificatonsForCandsHidden = isFieldHidden(
    t('MAX_QUALIFICATONS_FOR_CANDS'),
    13,
  );
  const estListHidden = isFieldHidden(t('EST_LIST'), 14);
  const estDetailHidden = isFieldHidden(t('EST_DETAIL'), 15);
  const allHidden =
    mostRecrtHidden &&
    jobsListBySectorHidden &&
    sectorsOrderHidden &&
    latestJobsHidden &&
    searchFieldLimitCharsHidden &&
    searchSpaceHidden &&
    similairJobsHidden &&
    candidatSignUpProfilePicHidden &&
    candidatSignUpTagsHidden &&
    candidatSignUpCvWrittenHidden &&
    delayCandLimitHidden &&
    delayFavsLimitHidden &&
    maxQualificatonsForCandsHidden &&
    estListHidden &&
    estDetailHidden;
  return (
    <>
      {(isMutateSettingsLoading || isSettingsFetching) ?? <Loading />}
      <Box mb={3}>
        <JTextFieldBase
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          attr={{ whiteBg: true }}
          label={t('SEARCH_SETTING')}
        />
      </Box>

      <Jpaper mb={3}>
        {allHidden && t('NOT_SETTINGS_FOUND')}
        <NumberSetting
          hidden={mostRecrtHidden}
          label={t('MOST_RECRT')}
          rank={1}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candEntRecruitMostMobile,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candEntRecruitMostMobile,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candEntRecruitMostMobile,
                String(value),
              );
            }}
            label={t('MOBILE')}
            reset={isSettingsFetching}
          />
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candEntRecruitMostDesktop,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candEntRecruitMostDesktop,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candEntRecruitMostDesktop,
                String(value),
              );
            }}
            label={t('DESKTOP')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={jobsListBySectorHidden}
          label={t('JOBS_LIST_BY_SECTOR')}
          rank={2}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candJobsByActivityAreaMobile,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candJobsByActivityAreaMobile,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candJobsByActivityAreaMobile,
                String(value),
              );
            }}
            label={t('MOBILE')}
            reset={isSettingsFetching}
          />
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candJobsByActivityAreaDesktop,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candJobsByActivityAreaDesktop,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candJobsByActivityAreaDesktop,
                String(value),
              );
            }}
            label={t('DESKTOP')}
            reset={isSettingsFetching}
          />
        </NumberSetting>
        {!sectorsOrderHidden && <RomeDomainsOrder />}
        <NumberSetting
          hidden={latestJobsHidden}
          label={t('LATEST_JOBS')}
          rank={4}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candLastJobsPublishedMobile,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candLastJobsPublishedMobile,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candLastJobsPublishedMobile,
                String(value),
              );
            }}
            label={t('MOBILE')}
            reset={isSettingsFetching}
          />
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candLastJobsPublishedDesktop,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candLastJobsPublishedDesktop,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candLastJobsPublishedDesktop,
                String(value),
              );
            }}
            label={t('DESKTOP')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={searchFieldLimitCharsHidden}
          label={`${t('SEARCH_FIELD')} ${t('LIMIT_CHARS')}`}
          rank={5}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candSearchBarMaxChars,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candSearchBarMaxChars,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candSearchBarMaxChars,
                String(value),
              );
            }}
            label={t('MOBILE_AND_DESKTOP')}
            reset={isSettingsFetching}
          />
        </NumberSetting>
        <NumberSetting
          hidden={searchSpaceHidden}
          label={`${t('SEARCH_SPACE')}`}
          rank={6}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candSearchAreaDefault,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candSearchAreaDefault,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candSearchAreaDefault,
                String(value),
              );
            }}
            label={t('RANGE_BY_DEFAULT')}
            reset={isSettingsFetching}
          />

          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candSearchAreaRange,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candSearchAreaRange,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candSearchAreaRange,
                String(value),
              );
            }}
            label={t('RAYON_INC')}
            reset={isSettingsFetching}
          />

          <JNumberField
            error={candSearchAreaRangeMinValidation}
            helperText={
              candSearchAreaRangeMinValidation && 'Error de validation'
            }
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candSearchAreaRangeMin,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candSearchAreaRangeMin,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candSearchAreaRangeMin,
                String(value),
              );
            }}
            label={t('RAYON_MIN')}
            reset={isSettingsFetching}
          />

          <JNumberField
            error={candSearchAreaRangeMaxValidation}
            helperText={
              candSearchAreaRangeMaxValidation && 'Error de validation'
            }
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candSearchAreaRangeMax,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candSearchAreaRangeMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candSearchAreaRangeMax,
                String(value),
              );
            }}
            label={t('RAYON_max')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={similairJobsHidden}
          label={t('SIMULAIR_JOBS')}
          rank={7}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candSimilarJobsMobile,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candSimilarJobsMobile,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candSimilarJobsMobile,
                String(value),
              );
            }}
            label={t('MOBILE')}
            reset={isSettingsFetching}
          />

          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candSimilarJobsDesktop,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candSimilarJobsDesktop,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candSimilarJobsDesktop,
                String(value),
              );
            }}
            label={t('DESKTOP')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={candidatSignUpProfilePicHidden}
          label={`${t('CANDIDAT_SIGN_UP')} : ${t('PROFILE_PIC')}`}
          rank={8}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candProfilePictureSizeMax,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candProfilePictureSizeMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candProfilePictureSizeMax,
                String(value),
              );
            }}
            label={t('SIZE_MO')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          caption={
            t('ADDRESS_AUTOCMPLETE_HIT', {
              charsCount: getDefaultValueByCode(
                CandidateSettingCodeEnum.candTagsAutocompleteCharacters,
              ),
              optionsCount: getDefaultValueByCode(
                CandidateSettingCodeEnum.candTagsListOptions,
              ),
            }) ?? ''
          }
          hidden={candidatSignUpTagsHidden}
          label={`${t('CANDIDAT_SIGN_UP')} : ${t('TAGS')}`}
          rank={9}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candTagsMax,
            )}
            newValue={getValueByCode(CandidateSettingCodeEnum.candTagsMax)}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candTagsMax,
                String(value),
              );
            }}
            label={t('MAX_TAGS')}
            reset={isSettingsFetching}
          />

          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candTagsAutocompleteCharacters,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candTagsAutocompleteCharacters,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candTagsAutocompleteCharacters,
                String(value),
              );
            }}
            label={t('CHARS')}
            reset={isSettingsFetching}
          />

          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candTagsListOptions,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candTagsListOptions,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candTagsListOptions,
                String(value),
              );
            }}
            label={t('OPTIONS')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={candidatSignUpCvWrittenHidden}
          label={`${t('CANDIDAT_SIGN_UP')} : ${t('CV_WRITTEN')}`}
          rank={10}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candCvFileSizeMax,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candCvFileSizeMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candCvFileSizeMax,
                String(value),
              );
            }}
            label={t('SIZE_MO')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <CheckboxSetting
          hidden={delayCandLimitHidden}
          reset={isSettingsFetching}
          defaultValue={getDefaultValueByCode(
            CandidateSettingCodeEnum.candApplicationsLimitPeriod,
          )}
          newValue={getValueByCode(
            CandidateSettingCodeEnum.candApplicationsLimitPeriod,
          ).toString()}
          rank={11}
          label={t('DELAY_CAND_LIMIT')}
          items={[
            { label: t('NO_LIMIT'), value: 'null' },
            { label: t('LAST_THREE_MONTHS'), value: '3' },
            { label: t('LAST_SIX_MONTHS'), value: '6' },
          ]}
          onChange={(value) =>
            onChangeSetting(
              CandidateSettingCodeEnum.candApplicationsLimitPeriod,
              String(value),
            )
          }
        />

        <CheckboxSetting
          hidden={delayFavsLimitHidden}
          reset={isSettingsFetching}
          defaultValue={getDefaultValueByCode(
            CandidateSettingCodeEnum.candFavLimitPeriod,
          )}
          newValue={getValueByCode(
            CandidateSettingCodeEnum.candFavLimitPeriod,
          ).toString()}
          rank={12}
          label={t('DELAY_FAVS_LIMIT')}
          items={[
            { label: t('NO_LIMIT'), value: 'null' },
            { label: t('LAST_THREE_MONTHS'), value: '3' },
            { label: t('LAST_SIX_MONTHS'), value: '6' },
          ]}
          onChange={(value) =>
            onChangeSetting(
              CandidateSettingCodeEnum.candFavLimitPeriod,
              String(value),
            )
          }
        />

        <NumberSetting
          hidden={maxQualificatonsForCandsHidden}
          label={t('MAX_QUALIFICATONS_FOR_CANDS')}
          rank={13}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candQualificationsMax,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candQualificationsMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candQualificationsMax,
                String(value),
              );
            }}
            label={t('QUALIFICATIONS')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting hidden={estListHidden} label={t('EST_LIST')} rank={14}>
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candMobileListEstablishmentElements,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candMobileListEstablishmentElements,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candMobileListEstablishmentElements,
                String(value),
              );
            }}
            label={t('ELMENT_PER_PAGE')}
            reset={isSettingsFetching}
          />
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candMobileListEstablishmentSearchMaxChars,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candMobileListEstablishmentSearchMaxChars,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candMobileListEstablishmentSearchMaxChars,
                String(value),
              );
            }}
            label={t('MAX_CHARS_SEARCH')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={estDetailHidden}
          label={t('EST_DETAIL')}
          rank={15}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              CandidateSettingCodeEnum.candMobileMaxJobsList,
            )}
            newValue={getValueByCode(
              CandidateSettingCodeEnum.candMobileMaxJobsList,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                CandidateSettingCodeEnum.candMobileMaxJobsList,
                String(value),
              );
            }}
            label={t('MAX_JOBS')}
            reset={isSettingsFetching}
          />
        </NumberSetting>
      </Jpaper>
    </>
  );
};

export default CandidatesSettings;
